import { Button, Checkbox, Flex, Modal, Table, Tag, Input } from "antd";
import React, { useEffect, useState } from "react";
import { IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";
import CustomHeader from "../../components/header/CustomHeader";
import ConfirmModal from "../../components/modal/ConfirmModal";
import api from "../../api";
import { useToast } from "../../components/toast/toast";
import { useAuth } from "../../store/context/AuthContext";

const Roles = () => {
  const toast = useToast();
  const { user } = useAuth();
  const canManage =
    user?.role === "super_admin" || user?.permissions?.includes("MANAGE_ROLE");

  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { id?, name, permissions }
  const [deleting, setDeleting] = useState(null); // устгах гэж буй role

  const load = async () => {
    setLoading(true);
    const [r, c] = await Promise.all([
      api.get_roles(),
      api.get_permission_catalog(),
    ]);
    if (r?.status === 200) setRoles(r.data.data);
    else toast(r?.data?.message || "Алдаа гарлаа", "error");
    if (c?.status === 200) setCatalog(c.data.data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    const body = { name: editing.name, permissions: editing.permissions };
    const res = editing.id
      ? await api.update_role(editing.id, body)
      : await api.create_role(body);
    if (res?.status === 200 || res?.status === 201) {
      toast("Амжилттай хадгаллаа", "success");
      setEditing(null);
      load();
    } else {
      toast(res?.data?.message || "Алдаа гарлаа", "error");
    }
  };

  const remove = async () => {
    if (!deleting) return;
    const res = await api.delete_role(deleting.id);
    if (res?.status === 200) {
      toast("Амжилттай устгалаа", "success");
      load();
    } else {
      toast(res?.data?.message || "Алдаа гарлаа", "error");
    }
    setDeleting(null);
  };

  const togglePerm = (perm, checked) =>
    setEditing((e) => ({
      ...e,
      permissions: checked
        ? [...e.permissions, perm]
        : e.permissions.filter((p) => p !== perm),
    }));

  const toggleGroup = (items, checked) => {
    const keys = items.map(([k]) => k);
    setEditing((e) => ({
      ...e,
      permissions: checked
        ? [...new Set([...e.permissions, ...keys])]
        : e.permissions.filter((p) => !keys.includes(p)),
    }));
  };

  const columns = [
    {
      title: "Role",
      dataIndex: "name",
      render: (v, r) => (
        <Flex gap={6} align="center">
          <b>{v}</b>
          {r.locked && <Tag color="gold">Бүх эрх</Tag>}
        </Flex>
      ),
    },
    {
      title: "Эрхийн тоо",
      render: (_, r) => (r.locked ? "Бүгд" : r.permissions.length),
    },
    { title: "Ажилтан", dataIndex: "user_count" },
    {
      title: "Үйлдэл",
      width: 120,
      render: (_, r) =>
        canManage && !r.locked ? (
          <Flex gap={4}>
            <Button
              type="text"
              icon={<IconEdit size={18} />}
              onClick={() =>
                setEditing({
                  id: r.id,
                  name: r.name,
                  permissions: r.permissions,
                })
              }
            />
            <Button
              type="text"
              danger
              icon={<IconTrash size={18} />}
              onClick={() => setDeleting(r)}
            />
          </Flex>
        ) : null,
    },
  ];

  return (
    <Flex vertical>
      <CustomHeader
        title="Эрхийн тохиргоо"
        rightSide={
          canManage && (
            <Button
              type="primary"
              style={{ padding: "0 30px" }}
              onClick={() => setEditing({ name: "", permissions: [] })}
            >
              <IconPlus size={16} /> Role нэмэх
            </Button>
          )
        }
      />

      <Table
        rowKey="id"
        columns={columns}
        dataSource={roles}
        loading={loading}
      />

      <Modal
        title={editing?.id ? "Role засах" : "Role нэмэх"}
        open={!!editing}
        onCancel={() => setEditing(null)}
        onOk={save}
        okText="Хадгалах"
        cancelText="Болих"
        okButtonProps={{ disabled: !editing?.name }}
        width={720}
      >
        <Flex vertical gap={16}>
          <div>
            <p style={{ marginBottom: 4 }}>Нэр (a-z, 0-9, _)</p>
            <Input
              value={editing?.name}
              placeholder="ж: accountant"
              onChange={(e) =>
                setEditing((s) => ({
                  ...s,
                  name: e.target.value.toLowerCase(),
                }))
              }
            />
          </div>

          {catalog.map(({ group, items }) => {
            const keys = items.map(([k]) => k);
            const checkedCount = keys.filter((k) =>
              editing?.permissions.includes(k),
            ).length;
            return (
              <div
                key={group}
                style={{
                  border: "1px solid #eee",
                  borderRadius: 6,
                  padding: 10,
                }}
              >
                <Checkbox
                  checked={checkedCount === keys.length}
                  indeterminate={checkedCount > 0 && checkedCount < keys.length}
                  onChange={(e) => toggleGroup(items, e.target.checked)}
                  style={{ fontWeight: 600, marginBottom: 6 }}
                >
                  {group}
                </Checkbox>
                <Flex wrap gap="6px 16px" style={{ paddingLeft: 24 }}>
                  {items.map(([key, label]) => (
                    <Checkbox
                      key={key}
                      checked={editing?.permissions.includes(key)}
                      onChange={(e) => togglePerm(key, e.target.checked)}
                    >
                      {label}
                    </Checkbox>
                  ))}
                </Flex>
              </div>
            );
          })}
        </Flex>
      </Modal>

      <ConfirmModal
        variant="error"
        showModal={!!deleting}
        setShowModal={(open) => !open && setDeleting(null)}
        cancelHandler={() => setDeleting(null)}
        submitHandler={remove}
        disabled={deleting?.user_count > 0}
        title="Эрх устгах уу?"
        description={
          deleting?.user_count > 0 ? (
            <>
              '
              <span style={{ fontWeight: 600, color: "#0066cc" }}>
                {deleting?.name}
              </span>
              ' эрхтэй <b>{deleting.user_count}</b> ажилтан байна. Эхлээд тэдний
              эрхийг солино уу.
            </>
          ) : (
            <>
              '
              <span style={{ fontWeight: 600, color: "#0066cc" }}>
                {deleting?.name}
              </span>
              ' эрхийг устгах үйлдэл нь буцаах боломжгүйг анхаарна уу!
            </>
          )
        }
      />
    </Flex>
  );
};

export default Roles;
